<template>
  <section class="box-layout">
    <div v-if="!isLoading" class="billing__content">
      <header class="content__header">
        <h2>{{ $t('userInfo.lnb.billing') }}</h2>
      </header>
      <div class="content__body" ref="scrollContent">
        <content-box class="payment-detail" :width="'370px'">
          <p class="title">{{ $t('userInfo.billing.paymentDetail.title') }}</p>

          <template v-if="cardInfo">
            <div class="card-number--wrap">
              <span class="label">{{ $t('userInfo.billing.paymentDetail.cardNumber') }}</span>
              <p class="value">
                <span>{{ cardInfo.cardNumber }} XXXX XXXX XXXX</span>
                <span class="card-name" :class="cardName">
                <template v-if="cardName === 'default'">
                  {{ cardInfo.cardName }}
                </template>
                <i v-else class="icon" :class="`icon--card-${cardName}`"></i>
              </span>
              </p>
            </div>
            <div class="register-date--wrap">
              <span class="label">{{ $t('userInfo.billing.paymentDetail.registeredDate') }}</span>
              <p class="value">{{ (cardInfo.updateDate || cardInfo.date) | dashDate }}</p>
            </div>
          </template>

          <template v-else>
            <p class="no-info">{{ $t('userInfo.billing.paymentDetail.noRegistered') }}</p>
          </template>

          <div class="ui-btn--wrap">
              <template v-if="cardInfo">
              <div v-if="!cardInfo.iamportCustomerUid && !cardInfo.tossBillingKey"
                   class="card-alert">
                <i class="icon icon--alert-triangle-size16"></i>
                <p class="balloon"
                   v-html="$t('userInfo.billing.paymentDetail.needUpdate')"></p>
              </div>
              <div v-else
                   class="card-alert">
                <i class="icon icon--info-normal"></i>
                <p class="balloon"
                   :class="$i18n.locale"
                   v-html="$t('userInfo.billing.paymentDetail.recurring')"></p>
              </div>
              <ui-button :size="'small'"
                         :primary2="true"
                         :disabled="isCardDisable"
                         @click="onRegisterCard">{{ $t('userInfo.billing.paymentDetail.update') }}</ui-button>
            </template>
            <ui-button v-else
                       :size="'small'"
                       :primary2="true"
                       :disabled="isCardDisable"
                       @click="onRegisterCard">{{ $t('userInfo.billing.paymentDetail.register') }}</ui-button>
          </div>
          <div class="event-tooltip" v-if="cardInfo && !cardInfo.isToss">
            <i class="arrow"></i>
            <p class="event-title">카드 재등록 이벤트 안내</p>
            <p class="event-desc">
              공인인증이 필요없는 간편 인증 방식으로 결제 시스템이 개편되었어요.
              카드를 재등록해주시면, 강력한 유료 기능을 이용할 수 있는 JAM을 선물로 드려요! (계정당 1회 제공)
            </p>
            <div class="event-guide">
              <p class="event-title">뷰저블 사이버 머니 JAM으로 뭘 할 수 있나요?</p>
              <a href="https://forum.beusable.net/ko/post/1321" target="_blank">
                결제 과정 없이 원하는 시점에 언제든 PV 충전
                <i class="icon icon--tooltip-link"></i>
              </a>
              <a href="https://forum.beusable.net/ko/post/1347" target="_blank">
                CX Heatmap으로 여정별 사용자 행동 분석 (Journey map)
                <i class="icon icon--tooltip-link"></i>
              </a>
            </div>
          </div>
        </content-box>

        <content-box class="billing-detail">
          <p class="title">{{ $t('userInfo.billing.billingDetail.title') }}</p>
          <div class="table--wrap" ref="listTable">
            <app-pagination class="billing-detail-pagination clearfix"
                            :total="paymentHistoryCount"
                            :page="page"
                            :limit="limit"
                            @onPagingOptionChange="onPagingOptionChange">
            </app-pagination>

            <data-table
              :headers="headers">
              <template v-slot:colgroup>
                <colgroup>
                  <col width="25%">
                  <col width="25%">
                  <col width="25%">
                  <col width="25%">
                </colgroup>
              </template>
              <template v-slot>
                <tbody v-if="paymentHistoryCount">
                <tr v-for="(item, index) in paymentHistoryList" :key="index">
                  <td class="data__date">{{ item.paymentDate | dashDate }}</td>
                  <td class="data__method">
                    <template v-if="cardInfo">
                      <i v-if="item.cardNumber === cardInfo.cardNumber"
                         class="icon"
                         :class="`icon--card-${cardName}`"></i>
                    </template>
                    <span class="card-num">{{ item.cardNumber }} XXXX XXXX XXXX</span>
                  </td>
                  <td class="data__total">₩{{ item.price | thousand }}</td>
                  <td class="data__receipt">
                    <ui-button :primary3="true" @click="onShowReceipt(item)">
                      {{ $t('common.view') }}
                    </ui-button>
                  </td>
                </tr>
                </tbody>
                <tbody v-else>
                <tr>
                  <td class="no-list" colspan="4">
                    {{ $t('userInfo.billing.billingDetail.noList') }}
                  </td>
                </tr>
                </tbody>
              </template>
            </data-table>
          </div>
        </content-box>
        <back-to-top :refName="'scrollContent'" />
      </div>
      <div v-show="showReceiptLayer" class="dimmed">
        <content-box class="receipt--wrap" :width="'480px'">
          <p class="title">{{ $t('common.invoice') }}</p>
          <iframe v-if="receiptUrl" ref="billIframe" id="billIframe"
                  style="width: 420px; height: 540px;"
                  :src="receiptUrl"></iframe>
          <i class="icon icon--close-size16" @click="onCloseReceiptLayer"></i>
        </content-box>
      </div>
      <register-card v-if="registerData"
                     :userData="userData"
                     :registerData="registerData"
                     @onRegisterSuccess="onRegisterSuccess"
                     @onRegisterCanceled="onRegisterCanceled"
                     @onRegisterFailed="onRegisterFailed"/>
    </div>
  </section>
</template>

<script>
import ContentBox from '@/components/ui/ContentBox'
import AppPagination from '@/components/ui/AppPagination'
import DataTable from '@/components/ui/DataTable'
import BackToTop from '@/components/ui/BackToTop'
import UiButton from '@/components/ui/UiButton'
import RegisterCard from '@/components/user-info/manage-plan/RegisterCard'
import { getCardInfo, getPaymentHistory, registerSuccessToss } from '@/api/card'
import { mapState } from 'vuex'
import { getDialog } from '@/libs/vue-utils'
import axios from 'axios'

const openDialog = getDialog()

export default {
  components: {
    ContentBox,
    AppPagination,
    DataTable,
    BackToTop,
    UiButton,
    RegisterCard
  },
  data () {
    return {
      isLoading: true,
      showReceiptLayer: false,
      receiptUrl: null,
      cardInfo: null,
      paymentHistory: null,
      headers: [
        {
          name: this.$t('common.table.header.date')
        },
        {
          name: this.$t('userInfo.billing.paymentDetail.paymentMethod')
        },
        {
          name: this.$t('common.total')
        },
        {
          name: this.$t('common.invoice')
        }
      ],
      page: 1,
      limit: 30,
      registerData: null
    }
  },
  watch: {
    showReceiptLayer (value) {
      if (value) {
        let body = document.querySelector('body')
        body.style.overflow = 'hidden'
      } else {
        let body = document.querySelector('body')
        body.style.overflow = 'initial'
      }
    }
  },
  computed: {
    ...mapState([
      'userData'
    ]),
    isCardDisable () {
      return ['en', 'ja'].includes(this.$i18n.locale)
    },
    paymentHistoryList () {
      if (!this.paymentHistory) {
        return []
      }
      const startIndex = (this.page - 1) * this.limit
      const endIndex = startIndex + this.limit
      return this.paymentHistory.slice(startIndex, endIndex)
    },
    paymentHistoryCount () {
      return this.paymentHistory ? this.paymentHistory.length : 0
    },
    cardName () {
      let cardName = this.cardInfo.cardName
      let className = 'default'
      if (cardName.indexOf('비씨 || BC') > -1 || cardName.indexOf('비씨') > -1 || cardName.indexOf('BC') > -1) {
        className = 'bc'
      } else if (cardName.indexOf('우리') > -1) {
        className = 'woori'
      } else if (cardName.indexOf('삼성') > -1) {
        className = 'samsung'
      } else if (cardName.indexOf('하나') > -1) {
        className = 'hana'
      } else if (cardName.indexOf('신한') > -1) {
        className = 'shinhan'
      } else if (cardName.indexOf('롯데') > -1) {
        className = 'lotte'
      } else if (cardName.indexOf('현대') > -1) {
        className = 'hyundai'
      } else if (cardName.indexOf('국민') > -1) {
        className = 'kb'
      } else if (cardName.indexOf('농협') > -1) {
        className = 'nh'
      } else if (cardName.indexOf('씨티') > -1 || cardName.indexOf('citi') > -1) {
        className = 'citi'
      } else {
        className = 'default'
      }
      return className
    }
  },
  methods: {
    setTossCard () {
      if (this.$route.query.customerKey && this.$route.query.authKey) {
        let secretKey = process.env.VUE_APP_TOSS_API_BILLLING_SEC
        secretKey = Buffer.from(secretKey).toString('base64')
        const url = process.env.VUE_APP_TOSS_API_URL + '/v1/billing/authorizations/issue'

        const headers = {
          Authorization: 'Basic ' + secretKey, // 예시 토큰
          'Content-Type': 'application/json'
        }
        axios.post(url, this.$route.query, { headers }).then(resp => {
          resp.data.isToss = true
          registerSuccessToss(resp.data).then(() => {
            this.setCardInfo()
            this.$router.push({ name: 'billing', query: {} })
          })
        })
      } else if (this.$route.query.code && this.$route.query.message) {
        openDialog({
          dialogType: 'plan.registerCardFailed',
          ok: this.$t('common.confirm')
        }).then(() => {
          this.$router.push({ name: 'billing', query: {} })
        })
      }
    },
    setCardInfo () {
      getCardInfo('beusable').then(response => {
        this.cardInfo = response
      })
    },
    setPaymentHistory () {
      getPaymentHistory().then(response => {
        this.paymentHistory = []
        for (let history of response) {
          if (history.cardNumber !== 'WIRE' && history.cardNumber !== '4grit' && history.cardNumber !== 'jam') {
            this.paymentHistory.push(history)
          }
        }
      })
    },
    onPagingOptionChange (page, limit) {
      this.page = page
      this.limit = limit
    },
    onShowReceipt (item) {
      this.showReceiptLayer = true
      if (item.data.pg_tid.indexOf('INIpayBillMOI4gritco') === 0) {
        this.receiptUrl = 'https://iniweb.inicis.com/DefaultWebApp/mall/cr/cm/mCmReceipt_head.jsp?noTid=' + item.paymentNum + '&noMethod=1'
      } else {
        this.receiptUrl = item.data.receipt.url
      }
    },
    onCloseReceiptLayer () {
      this.receiptUrl = null
      this.showReceiptLayer = false
    },
    loadData () {
    },
    onRegisterCard () {
      this.registerData = {
        customerUid: this.cardInfo ? this.cardInfo.iamportCustomerUid : null
      }
    },
    onRegisterSuccess (result) {
      this.registerData = null
      this.cardInfo = result
    },
    onRegisterCanceled () {
      this.registerData = null
    },
    onRegisterFailed (result) {
      this.registerData = null

      openDialog({
        dialogType: 'plan.registerCardFailed',
        ok: this.$t('common.confirm')
      })
    }
  },
  created () {
    Promise.all([
      this.setTossCard(),
      this.setCardInfo(),
      this.setPaymentHistory()
    ]).then(() => {
      this.isLoading = false
    }).catch(() => {
      this.isLoading = false
    })
  }
}
</script>

<style lang="scss" scoped="scoped">
  .box-layout {
    position: relative;
    float: left;
    width: calc(100% - 211px);
    margin-top: 40px;
  }
  .billing__content {
    .content__header {
      font-size: 26px;
      font-weight: 500;
      color: #444444;
      margin-left: 30px;
      margin-right: 30px;
      padding-bottom: 22px;
      border-bottom: 1px solid #d7d7d7;

      h2 {
        padding-left: 10px;
      }
    }

    .content__body {
      overflow-y: auto;
      height: calc(100vh - 166px);
      padding: 30px 30px 0;

      .content--wrap {
        border-radius: 8px;

        .title {
          font-size: 15px;
          font-weight: 600;
          color: #2f2f2f;
        }
      }

      .payment-detail {
        position: relative;
        padding: 25px 20px 20px 25px;
        .label {
          font-size: 12px;
          color: #767676;
        }
        .value {
          margin-top: 8px;
        }

        .card-number--wrap {
          margin-top: 58px;
          .value {
            @include flexXbetween;
            align-items: center;
            font-size: 17px;
            font-weight: 600;
            color: #444444;

            .card-name {
              text-align: right;
              width: 115px;
              font-size: 14px;
              line-height: 20px;

              display: -webkit-box;
              -webkit-line-clamp: 3;
              -webkit-box-orient: vertical;
              overflow: hidden;

              .icon {
                width: 80px;
                height: 22px;
                background-size: contain;
              }
            }
          }
        }
        .register-date--wrap {
          margin-top: 22px;
          .value {
            font-size: 14px;
            color: #2f2f2f;
          }
        }

        .ui-btn--wrap {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 5px;

          /deep/ .ui-btn {
            .text {
              font-size: 13px;
            }
          }

          .card-alert {
            position: relative;

            .balloon {
              position: absolute;
              bottom: 33px;
              left: 50%;
              width: 278px;
              transform: translateX(-50%);
              color: #444444;
              line-height: 1.46;
              font-size: 13px;
              display: none;
              padding: 16px 20px;
              background: #fff;
              border-radius: 10px;
              box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
              box-sizing: border-box;

              &.ja {
                width: 275px;
              }

              &.en {
                width: 295px;
              }

              &::after {
                content: "";
                background: #fff;
                border-right: 1px solid rgba(0, 0, 0, 0.27);
                border-bottom: 1px solid rgba(0, 0, 0, 0.27);
                width: 10px;
                height: 10px;
                margin-left: -5px;
                position: absolute;
                bottom: -6px;
                left: 50%;
                transform: rotate(45deg);
              }
            }

            &:hover .balloon {
              display: block;
            }
          }

          button {
            min-width: 114px;
            width: auto;
          }
        }

        .no-info {
          margin-top: 63px;
          margin-bottom: 82px;
          text-align: center;
          font-weight: 500;
        }
        .event-tooltip {
          position: absolute;
          z-index: 5;
          border-radius: 8px;
          -webkit-box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
          box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
          background-color: #ffffff;
          padding: 16px 20px;
          box-sizing: border-box;
          opacity: 1;
          width: 413px;
          height: 201px;
          left: 104%;
          top: 11%;
          font-size: 13px;
          line-height: 144%;
          color: #444;

          .arrow {
            content: '';
            background-color: #fff;
            position: absolute;
            z-index: 2;
            width: 10px;
            height: 10px;
            -webkit-box-shadow: 1px -1px 0 0 rgba(0, 0, 0, 0.16);
            box-shadow: 1px -1px 0 0 rgba(0, 0, 0, 0.16);
            left: 0;
            top: 78%;
            transform: rotate(-135deg) translateY(-50%);
          }

          p {
            white-space: nowrap;
            display: inline-block;
            text-align: left;
          }

          .event-title {
            font-weight: 600;
            margin-bottom: 8px;
          }

          .event-desc {
            word-break: keep-all;
            white-space: normal;
          }

          .event-guide {
            margin-top: 18px;
            display: flex;
            flex-direction: column;
            gap: 3px;

            a {
              font-size: 13px;
              font-weight: 400;
              color: #0074FF;
              text-decoration: none;
              display: flex;
              align-items: center;
              gap: 5px;

              .icon--tooltip-link {
                transform: rotate(90deg);
              }
            }
          }
        }
      }

      .billing-detail {
        margin-top: 30px;
        padding: 25px 25px 30px;

        .title {
          padding-bottom: 20px;
        }

        .billing-detail-pagination {
          margin-bottom: 15px;
        }

        .table--wrap {
          text-align: center;

          /deep/ thead {
            font-size: 13px;
          }

          tbody tr {
            border-bottom: 1px solid #ebebeb;

            td {
              height: 50px;
              font-size: 13px;
              line-height: 1.23;
              color: #2f2f2f;
              font-weight: 500;

              &.no-list {
                height: 199px;
                color: #767676;
              }

              &.data__method {
                .icon {
                  width: 80px;
                  height: 22px;
                  background-size: contain;
                  vertical-align: middle;
                  margin-right: 5px;
                }
                .card-num {
                  display: inline-block;
                  vertical-align: middle;
                }
              }

              &.data__receipt {
                .ui-btn.size--normal {
                  height: 34px;
                  font-size: 13px;
                  min-width: 80px;
                }
              }
            }
          }
        }
      }
    }

    .dimmed {
      .receipt--wrap {
        @include positionXYcenter;
        text-align: center;
        padding: 30px;

        .title {
          text-align: left;
          font-size: 15px;
          font-weight: 600;
          color: #2f2f2f;
          margin-bottom: 25px;
        }

        .icon {
          position: absolute;
          top: 12px;
          right: 12px;
        }
      }
    }
  }
</style>
